import { Injectable } from '@angular/core';
import { addDoc, collection, doc, DocumentReference, getFirestore, setDoc, UpdateData, updateDoc, writeBatch } from '@angular/fire/firestore';
import { Auditable, Dto } from '@damofire/data-commons';
import { AuthenticationService } from './authentication.service';
import { v4 as uuidv4 } from 'uuid';
import { WithFieldValue } from '@firebase/firestore';

@Injectable()
export class AuthDtoSignerService {
  constructor(private readonly authService: AuthenticationService) {}

  generateId(): string {
    return uuidv4();
  }

  async add<T extends Dto | unknown>(dto: WithFieldValue<T>, path: string): Promise<DocumentReference> {
    console.log(`Signing object and adding to collection: ${path}`, dto);
    await this.sign(dto, true);
    const col = collection(getFirestore(), path);
    return addDoc(col, dto as any);
  }

  async addAll<T extends Dto | unknown>(dtos: T[], path: string): Promise<void> {
    for (const dto of dtos) {
      await this.add(dto, path);
    }
  }

  async setAll<T extends Dto>(dtos: T[], collectionName: string): Promise<void> {
    for (const dto of dtos) {
      await this.set(dto, `${collectionName}/${dto._id}`);
    }
  }

  async groupAdd<T>(dto: T, path: string, ...pathSegments: string[]): Promise<DocumentReference> {
    console.log(`Signing object and adding to collection group: ${path}/${pathSegments.join('/')}`);
    await this.sign(dto, true);
    const col = collection(getFirestore(), path, ...pathSegments);
    return addDoc(col, dto);
  }

  async set<T extends Dto | unknown>(dto: T, docPath: string): Promise<DocumentReference> {
    console.log(`Signing object and setting document at: ${docPath}`);
    await this.sign(dto, true);

    const docRef = doc(getFirestore(), docPath);
    await setDoc(docRef, dto);
    return docRef;
  }

  async update<T extends Dto | unknown>(dto: UpdateData<T>, docPath: string): Promise<DocumentReference> {
    console.log(`Signing object and updating doc: ${docPath}`);
    await this.sign(dto);

    const docRef = doc(getFirestore(), docPath);
    await updateDoc(docRef, dto as any);
    return docRef;
  }

  async updateAll<T extends Dto | unknown>(dtos: { data: UpdateData<T>; docPath: string }[]): Promise<void> {
    const firestore = getFirestore();
    const batch = writeBatch(firestore);

    for (const dto of dtos) {
      console.log(`Signing object and updating doc: ${dto.docPath}`);
      await this.sign(dto);
      const docRef = doc(firestore, dto.docPath);
      batch.update(docRef, dto.data as any);
    }

    return batch.commit();
  }

  async sign<T extends Auditable>(auditable: T | any, create = false, flat = true): Promise<T> {
    const user = await this.authService.currentUser();

    if (create) {
      auditable.audit = {
        createdBy: user._id,
        createdDate: new Date(),
        lastModifiedBy: user._id,
        lastModifiedDate: new Date(),
      };
    } else {
      if (flat) {
        auditable['audit.lastModifiedBy'] = user._id;
        auditable['audit.lastModifiedDate'] = new Date();
      } else {
        auditable.audit = auditable.audit || {};
        auditable.audit.lastModifiedBy = user._id;
        auditable.audit.lastModifiedDate = new Date();
      }
    }

    return auditable;
  }
}
